/**
 * "New mockup" and "Upload a new version" — the studio's one upload, in two places
 * (session 48).
 *
 * Three steps, and the middle one is why it is not one request: `mockups_create` (a new
 * mockup only), `mockups_create_upload` for a one-time URL, then a `PUT` of the file to it —
 * the files module's arrangement and `ARCHITECTURE.md` §Catalogue's rule that bytes go over
 * HTTP and not through a tool. The `PUT` carries no session: the token is its whole
 * authentication, which is also what lets an agent do the same thing from a terminal.
 *
 * The upload's warnings — a path starting with "/" — are shown before the dialog closes,
 * because they are the one thing about a version that is wrong and still stored.
 */

import {
  MOCKUP_UPLOAD_MAX_BYTES,
  MockupVersionNoteSchema,
  type MockupUploadResult,
} from '@fareast/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'

import { moduleKey } from '../../api/keys.js'
import { messageOf, problemOf } from '../../api/problem.js'
import { useApi } from '../../api/provider.js'
import { reviewOf, useStudioAction } from '../../api/studio.js'
import { Button, ButtonRow } from '../../components/portal/Button.js'
import {
  Check,
  Drop,
  Field,
  Hint,
  Input,
  Textarea,
  useZodForm,
} from '../../components/portal/Form.js'
import { Stack } from '../../components/portal/Layout.js'
import { Dialog } from '../../components/portal/Overlay.js'
import { Notice } from '../../components/portal/Status.js'
import { B, Prose, Small } from '../../components/portal/Text.js'
import { useFormat } from '../../format/useFormat.js'

export type UploadMockupTarget =
  | { readonly kind: 'new'; readonly client: string }
  | { readonly kind: 'version'; readonly mockup: string }

const UploadSchema = z.object({
  title: z.string().trim().max(120),
  note: MockupVersionNoteSchema,
  request_review: z.boolean(),
})

type UploadValues = z.infer<typeof UploadSchema>

export function UploadMockupButton({
  target,
  onDone,
}: {
  readonly target: UploadMockupTarget
  readonly onDone?: ((ref: string) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const label = target.kind === 'new' ? 'New mockup' : 'Upload a new version'

  return (
    <>
      <Button
        variant={target.kind === 'new' ? 'ochre' : 'default'}
        onClick={() => {
          setOpen(true)
        }}
      >
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title={label}>
        {open ? (
          <UploadForm
            target={target}
            onClose={(ref) => {
              setOpen(false)
              if (ref !== null) onDone?.(ref)
            }}
          />
        ) : null}
      </Dialog>
    </>
  )
}

function UploadForm({
  target,
  onClose,
}: {
  readonly target: UploadMockupTarget
  readonly onClose: (ref: string | null) => void
}) {
  const format = useFormat()
  const api = useApi()
  const queryClient = useQueryClient()
  const create = useStudioAction('mockups_create')
  const reserve = useStudioAction('mockups_create_upload')
  const [picked, setPicked] = useState<File | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<MockupUploadResult | null>(null)
  /**
   * The mockup a first press made, kept for the next one. A refused upload — a zip with no
   * index.html — leaves the mockup made and empty; pressing Upload again with the right file
   * must put the version on that one, not make a second.
   */
  const [made, setMade] = useState<string | null>(null)

  const form = useZodForm(UploadSchema, {
    defaultValues: { title: '', note: '', request_review: true },
  })

  if (done !== null) {
    return (
      <Stack>
        <Prose>
          <B>Version {format.number(done.version.number)}</B> of {done.mockup} is up
          {done.version.review.state === 'requested'
            ? ', and the client has been asked for their answer.'
            : '.'}
        </Prose>
        {done.warnings.map((warning) => (
          <Notice key={warning} need role="status">
            {warning}
          </Notice>
        ))}
        <ButtonRow>
          <Button
            variant="primary"
            onClick={() => {
              onClose(done.mockup)
            }}
          >
            Done
          </Button>
        </ButtonRow>
      </Stack>
    )
  }

  const send = async (values: UploadValues) => {
    setFailure(null)
    if (picked === null) {
      setFailure('Choose the .html file or the .zip first.')
      return
    }
    if (target.kind === 'new' && values.title === '') {
      setFailure('Give the mockup a title — it is what the client reads.')
      return
    }
    setBusy(true)
    try {
      let ref: string
      if (target.kind === 'version') {
        ref = target.mockup
      } else if (made !== null) {
        ref = made
      } else {
        const answer = await create.mutateAsync({ client: target.client, title: values.title })
        if (reviewOf(answer) !== null || !('mockup' in answer)) {
          throw new Error('The mockup was held for review and not made.')
        }
        ref = answer.mockup.ref
        setMade(ref)
      }

      const ticket = await reserve.mutateAsync({
        mockup: ref,
        note: values.note,
        request_review: values.request_review,
      })
      if (reviewOf(ticket) !== null || !('upload_url' in ticket)) {
        throw new Error('The upload was held for review and not made.')
      }

      // The token is the authentication: no session, and no JSON.
      const response = await api.fetch(new URL(ticket.upload_url).pathname, {
        method: 'PUT',
        body: picked,
        headers: { 'Content-Type': picked.type === '' ? 'application/octet-stream' : picked.type },
      })
      if (!response.ok) throw await problemOf(response)
      const answer = (await response.json()) as MockupUploadResult

      await queryClient.invalidateQueries({ queryKey: moduleKey('mockups') })
      if (answer.warnings.length === 0) onClose(answer.mockup)
      else setDone(answer)
    } catch (error) {
      setFailure(messageOf(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit(send)(event)
      }}
    >
      <Stack>
        <Prose>
          One self-contained <B>.html</B> file, or a <B>.zip</B> with an index.html and its CSS,
          scripts, images and fonts beside it. Every upload is a new version; nothing replaces an
          old one.
        </Prose>

        {target.kind === 'new' ? (
          <Input
            control={form.control}
            name="title"
            label="Title"
            placeholder="Homepage redesign"
            hint="What the client reads, in the list and in the mail."
          />
        ) : null}

        <Textarea
          control={form.control}
          name="note"
          label="What this version is"
          placeholder="Hero shortened, pricing table added"
        />

        <Check
          control={form.control}
          name="request_review"
          label="Ask the client for their answer"
        />

        <Field>
          <Small>
            <B>The file</B>
          </Small>
          <Drop
            label="Drop the .html or .zip, or"
            accept=".html,.htm,.zip,text/html,application/zip"
            hint={`Up to ${format.bytes(MOCKUP_UPLOAD_MAX_BYTES)}. Paths inside it must be relative — "images/hero.png", not "/images/hero.png".`}
            onFiles={(files) => {
              setPicked(files[0] ?? null)
              setFailure(null)
            }}
          />
          {picked === null ? null : <Hint>{picked.name}</Hint>}
        </Field>

        {failure === null ? null : (
          <Notice need role="alert">
            {failure}
          </Notice>
        )}

        <ButtonRow>
          <Button type="submit" variant="ochre" disabled={busy || picked === null}>
            {busy ? 'Uploading…' : 'Upload it'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              onClose(null)
            }}
          >
            Cancel
          </Button>
        </ButtonRow>
      </Stack>
    </form>
  )
}
